import { useState, useEffect } from "react";
import { X, Loader2, Check, LogIn, Globe2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { postCheckout, postPolarCheckout, fetchPolarConfig, fetchLocalizedPricing } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

/**
 * PricingModal — full-screen paywall / upgrade CTA.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   reason: "quota_exhausted" | "manual" — determines headline copy
 *   playsRemaining: number (0 when reason=quota_exhausted)
 */
export default function PricingModal({ open, onClose, reason = "manual", playsRemaining = 0 }) {
  const { user, showSignIn } = useAuth();
  const [busy, setBusy] = useState(null); // "stripe" | "polar" | null
  const [polarEnabled, setPolarEnabled] = useState(false);
  const [pricing, setPricing] = useState(null); // localised price object

  // Discover whether Polar has been configured server-side + fetch localised
  // pricing so visitors from India see "₹499" rather than "$6.99".
  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchPolarConfig().then((c) => {
      if (alive) setPolarEnabled(Boolean(c?.enabled));
    });
    fetchLocalizedPricing().then((p) => {
      if (alive) setPricing(p);
      if (p?.is_local) trackEvent("pricing_localized_view", { country: p.country, currency: p.currency });
    });
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;

  const requireLogin = () => {
    trackEvent("upgrade_signin_required", { reason });
    // Preserve upgrade intent across auth. After sign-in the browser lands
    // back on this page with `?upgrade=1`, which Landing detects and
    // auto-reopens the pricing modal so the user can actually pay.
    const url = new URL(window.location.href);
    url.searchParams.set("upgrade", "1");
    if (reason) url.searchParams.set("reason", reason);
    const returnPath = url.pathname + url.search;
    showSignIn(returnPath);
  };

  const startStripe = async () => {
    if (!user) return requireLogin();
    setBusy("stripe");
    trackEvent("upgrade_checkout_start", { reason, provider: "stripe" });
    try {
      const { checkout_url } = await postCheckout({
        lookup_key: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      handleCheckoutError(e);
      setBusy(null);
    }
  };

  const startPolar = async () => {
    if (!user) return requireLogin();
    setBusy("polar");
    trackEvent("upgrade_checkout_start", { reason, provider: "polar" });
    try {
      const { checkout_url } = await postPolarCheckout({
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      handleCheckoutError(e);
      setBusy(null);
    }
  };

  const handleCheckoutError = (e) => {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 401 || detail?.code === "login_required") {
      showSignIn(window.location.pathname + window.location.search);
      return;
    }
    toast.error(typeof detail === "string" ? detail : detail?.message || "Could not start checkout");
  };

  const headline =
    reason === "quota_exhausted"
      ? "You've used all 2 free plays today"
      : "Go premium. Watch history without limits.";
  const kicker =
    reason === "quota_exhausted"
      ? "The archives close for the day at midnight UTC — or unlock unlimited playback now."
      : "Two free Cinema Mode episodes every day, forever. Upgrade for the full binge.";

  const perks = [
    "Unlimited Cinema Mode episodes",
    "Ad-free lean-back playback",
    "Auto-advancing Era Binge playlists",
    "Priority AI narration (no queue)",
    "Cancel anytime — full self-serve portal",
  ];

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/85 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm"
      data-testid="pricing-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-paper text-ink border-2 border-ink shadow-[8px_8px_0_#1A1C20] max-h-[95vh] overflow-y-auto"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-ink flex items-center justify-center hover:bg-ink hover:text-paper"
          aria-label="Close"
          data-testid="pricing-close-btn"
        >
          <X size={16} />
        </button>

        {/* Top strip */}
        <div className="border-b-2 border-ink px-6 sm:px-10 py-4 bg-paper-alt flex items-center gap-3">
          <span className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-press-red">
            ✦ TimeCapsule 24 · Premium
          </span>
          {reason === "quota_exhausted" && (
            <span className="ml-auto font-mono uppercase text-[10px] sm:text-xs bg-press-red text-paper px-2 py-1">
              Limit reached
            </span>
          )}
        </div>

        <div className="px-6 sm:px-10 py-8 sm:py-10">
          <h1
            className="font-headline uppercase font-black leading-[0.95] tracking-tighter text-3xl sm:text-5xl lg:text-6xl"
            data-testid="pricing-headline"
          >
            {headline}
          </h1>
          <p className="mt-4 font-body text-base sm:text-lg text-ink-muted italic max-w-xl">
            {kicker}
          </p>

          {/* Plan card */}
          <div className="mt-8 border-2 border-ink p-6 sm:p-8 bg-paper-soft">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                className="font-headline text-4xl sm:text-5xl font-black"
                data-testid="pricing-price-primary"
              >
                {pricing?.is_local ? pricing.price_display : "$6.99"}
              </span>
              <span className="font-mono uppercase text-xs sm:text-sm text-ink-muted">/month</span>
              {pricing?.is_local && (
                <span
                  className="font-mono text-[10px] sm:text-xs text-ink-muted"
                  data-testid="pricing-usd-equivalent"
                >
                  ≈ {pricing.usd_display} USD
                </span>
              )}
            </div>
            <div className="font-mono uppercase tracking-widest text-[10px] sm:text-xs text-ink-muted mt-1">
              {pricing?.is_local
                ? `Local pricing for ${pricing.country_name} · Billed monthly · Cancel anytime`
                : "Billed monthly · cancel anytime"}
            </div>

            <ul className="mt-6 space-y-2.5">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 font-body text-sm sm:text-base">
                  <Check size={16} className="mt-0.5 text-press-red flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            {!user ? (
              <>
                <button
                  onClick={requireLogin}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:border-ink"
                  data-testid="pricing-checkout-btn"
                >
                  <LogIn size={16} />
                  Sign in to continue →
                </button>
                <p className="mt-4 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-ink-muted text-center">
                  Sign in first so your Premium sticks to your account.
                </p>
              </>
            ) : (
              <div className="mt-8 space-y-3" data-testid="pricing-provider-choices">
                {polarEnabled && (
                  <button
                    onClick={startPolar}
                    disabled={busy !== null}
                    className="w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:border-ink disabled:opacity-60 disabled:cursor-wait"
                    data-testid="pricing-polar-btn"
                  >
                    {busy === "polar" ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Globe2 size={16} />
                    )}
                    {busy === "polar" ? "Redirecting to Polar…" : "Pay with local methods · Polar →"}
                  </button>
                )}
                <button
                  onClick={startStripe}
                  disabled={busy !== null}
                  className={
                    polarEnabled
                      ? "w-full inline-flex items-center justify-center gap-2 bg-paper text-ink border-2 border-ink px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:text-paper disabled:opacity-60 disabled:cursor-wait"
                      : "w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-ink hover:border-ink disabled:opacity-60 disabled:cursor-wait"
                  }
                  data-testid="pricing-checkout-btn"
                >
                  {busy === "stripe" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CreditCard size={16} />
                  )}
                  {busy === "stripe"
                    ? "Redirecting to Stripe…"
                    : polarEnabled
                      ? "Pay with card · Stripe"
                      : "Continue to secure checkout →"}
                </button>
                <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-ink-muted text-center">
                  {polarEnabled
                    ? "Polar unlocks UPI, Pix, iDEAL, SEPA, Apple Pay & more · picked by your region."
                    : "Payments handled by Stripe · Test card 4242 4242 4242 4242"}
                </p>
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="mt-5 w-full font-mono uppercase tracking-widest text-[10px] sm:text-xs text-ink-muted hover:text-ink underline underline-offset-4"
            data-testid="pricing-maybe-later-btn"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
